const compareOrganizations = (left, right) => {
  const leftName = (left.name || '').toLowerCase();
  const rightName = (right.name || '').toLowerCase();
  if (leftName < rightName) {
    return -1;
  }
  if (leftName > rightName) {
    return 1;
  }

  const leftId = left.masked_id || '';
  const rightId = right.masked_id || '';
  if (leftId < rightId) {
    return -1;
  }
  if (leftId > rightId) {
    return 1;
  }
  return 0;
};

const findCyclicOrganizationIds = organizationsById => {
  const cyclicIds = new Set();

  organizationsById.forEach((organization, organizationId) => {
    const path = [];
    const positions = new Map();
    let currentId = organizationId;

    while (organizationsById.has(currentId)) {
      if (positions.has(currentId)) {
        path.slice(positions.get(currentId)).forEach(id => cyclicIds.add(id));
        break;
      }

      positions.set(currentId, path.length);
      path.push(currentId);
      currentId = organizationsById.get(currentId)?.parent_org_id;
    }
  });

  return cyclicIds;
};

export const buildOrganizationTree = organizations => {
  const organizationsById = new Map(
    (organizations || [])
      .filter(organization => organization?.masked_id)
      .map(organization => [organization.masked_id, organization]),
  );
  const cyclicIds = findCyclicOrganizationIds(organizationsById);
  const nodesById = new Map(
    Array.from(organizationsById, ([id, organization]) => [id, { organization, children: [] }]),
  );
  const roots = [];

  nodesById.forEach((node, id) => {
    const parentId = node.organization.parent_org_id;
    const parentNode = nodesById.get(parentId);

    if (!parentNode || cyclicIds.has(id)) {
      roots.push(node);
      return;
    }

    parentNode.children.push(node);
  });

  const sortNodes = nodes => {
    nodes.sort((left, right) => compareOrganizations(left.organization, right.organization));
    nodes.forEach(node => sortNodes(node.children));
  };

  sortNodes(roots);
  return roots;
};

export const getMatchingOrganizationIdsWithAncestors = (organizations, predicate) => {
  const organizationsById = new Map(
    (organizations || [])
      .filter(organization => organization?.masked_id)
      .map(organization => [organization.masked_id, organization]),
  );
  const visibleIds = new Set();

  organizationsById.forEach(organization => {
    if (!predicate(organization)) {
      return;
    }

    let current = organization;
    const pathIds = new Set();
    while (current && !pathIds.has(current.masked_id)) {
      pathIds.add(current.masked_id);
      visibleIds.add(current.masked_id);
      current = organizationsById.get(current.parent_org_id);
    }
  });

  return visibleIds;
};

export const flattenOrganizationTree = (
  tree,
  { collapsedIds = new Set(), includedIds, ignoreCollapsed = false } = {},
) => {
  const flattened = [];

  const visit = (node, depth) => {
    const organizationId = node.organization.masked_id;
    if (includedIds && !includedIds.has(organizationId)) {
      return;
    }

    const visibleChildren = includedIds
      ? node.children.filter(child => includedIds.has(child.organization.masked_id))
      : node.children;
    flattened.push({
      ...node.organization,
      hierarchyDepth: depth,
      hasHierarchyChildren: visibleChildren.length > 0,
    });

    if (!ignoreCollapsed && collapsedIds.has(organizationId)) {
      return;
    }

    visibleChildren.forEach(child => visit(child, depth + 1));
  };

  (tree || []).forEach(root => visit(root, 0));
  return flattened;
};

export const getOrganizationHierarchyRows = organizations => {
  const flattenedOrganizations = flattenOrganizationTree(buildOrganizationTree(organizations));
  const organizationsById = new Map(
    flattenedOrganizations.map(organization => [organization.masked_id, organization]),
  );
  const branchIdsByOrganizationId = new Map(
    flattenedOrganizations.map(organization => [organization.masked_id, []]),
  );
  const ancestorIdsByOrganizationId = new Map();
  const ancestorIds = [];

  flattenedOrganizations.forEach(organization => {
    ancestorIds.length = organization.hierarchyDepth;
    const organizationId = organization.masked_id;

    ancestorIdsByOrganizationId.set(organizationId, [...ancestorIds]);
    branchIdsByOrganizationId.get(organizationId).push(organizationId);
    ancestorIds.forEach(ancestorId => {
      branchIdsByOrganizationId.get(ancestorId).push(organizationId);
    });
    ancestorIds.push(organizationId);
  });

  return flattenedOrganizations.map(organization => {
    const hierarchyAncestorIds = ancestorIdsByOrganizationId.get(organization.masked_id);
    const nestedBranchDepth = hierarchyAncestorIds.filter(ancestorId => {
      const ancestor = organizationsById.get(ancestorId);
      return ancestor.hierarchyDepth > 0 && ancestor.hasHierarchyChildren;
    }).length;
    const hierarchyBranchDepth = organization.hierarchyDepth + nestedBranchDepth;

    return {
      ...organization,
      hierarchyAncestorIds,
      hierarchyBranchIds: branchIdsByOrganizationId.get(organization.masked_id),
      hierarchyBranchDepth,
      hierarchyOptionDepth: hierarchyBranchDepth + (organization.hasHierarchyChildren ? 1 : 0),
    };
  });
};
